import React, { ChangeEvent, useState } from "react";
import { DescriptionAdderProps } from "./types";
import LoadingScreen from "../../../screens/LoadingScreen";
import InputField from "../../misc/InputField";
import { Button } from "../../misc/Button";
import { getAuthHeader } from "../../../utils/authHeader";
import axios from "axios";
import { toast } from "react-toastify";
import CustomToast from "../../misc/CustomToast";
import { useNavigate } from "react-router-dom";
import PlanLimitReached from "../../misc/PlanLimitReached";
import AutopilotOwnListingBlocked from "../../misc/AutopilotOwnListingBlocked";
import AutopilotTypeNotSyncedBlocked from "../../misc/AutopilotTypeNotSyncedBlocked";
import {
    getApiRefusalMessage,
    getBlockedPropertyTypeLabel,
    isAutopilotOwnListingBlock,
    isAutopilotTypeNotSyncedBlock,
    isMlsDisplayBlock,
    getMlsFeedOutageMessage,
} from "../../../utils/apiError";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import { setUser } from "../../../store/authSlice";
import { useAutopilotEnabled } from "../../../hooks/useAutopilotEnabled";

const DescriptionAdder: React.FC<DescriptionAdderProps> = ({
    isMobile,
    listing,
    setListing,
    setCurrentStep,
    mlsId,
    saleType,
    sharedListing,
    saveMlsAgentDetails,
}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const currentUser = useAppSelector((state) => state.auth.user);
    // MB-1442: this is the second create path - ConfirmUserListing only posts
    // directly when the MLS remarks reach 500 characters, otherwise the wizard
    // lands here. Both need the shared-listing confirmation, or a terse-remarks
    // listing gets the generic copy and the user hunts for the Create Campaign
    // button MB-1441 hides.
    const { autopilotEnabled } = useAutopilotEnabled();

    const [loading, setLoading] = useState(false);

    const [planLimitOpen, setPlanLimitOpen] = useState<boolean>(false);
    // MB-1424: hard block - the user's own listing while Autopilot is on.
    const [autopilotBlockOpen, setAutopilotBlockOpen] =
        useState<boolean>(false);
    // MB-1506: same block, but the property type is not auto-synced and the
    // listing is nowhere in the review lists - so it needs the how-to copy.
    const [typeNotSyncedOpen, setTypeNotSyncedOpen] = useState<boolean>(false);
    const [blockedPropertyType, setBlockedPropertyType] = useState<
        string | null
    >(null);

    const handleInputChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        if (!listing) return;

        setListing({
            ...listing,
            [name]:
                name === "Bedrooms" ||
                name === "Bathrooms" ||
                name === "Square Footage" ||
                name === "Year Built"
                    ? parseInt(value, 10)
                    : name === "lotSize"
                    ? parseFloat(value)
                    : name === "currentPrice"
                    ? parseFloat(value)
                    : value,
        });
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const headers = await getAuthHeader();
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const payload = {
                mlsId: mlsId || listing?._id,
                footer: listing?.footer,
                latitude: listing?.latitude,
                longitude: listing?.longitude,
                formattedAddress: listing?.formattedAddress,
                additionalContent: listing?.additionalContent,
                saleType: saleType,
                sharedListing: sharedListing,
                saveMlsAgentDetails: saveMlsAgentDetails ?? false,
            };
            const response = await axios.post<{ mlsSetupPending?: number }>(
                `${backendUrl}/listing/create`,
                payload,
                {
                    headers,
                    withCredentials: true,
                }
            );

            if (response.status === 200 || response.status === 201) {
                // When MLS details were saved, the user doc gained the agent
                // identity + autoIngestEnabled — refetch so Redux (and the MLS
                // Setting page) reflect it without a manual reload.
                if (saveMlsAgentDetails) {
                    try {
                        const fresh = await axios.get(
                            `${backendUrl}/settings/personal-info`,
                            { headers }
                        );
                        if (currentUser) {
                            dispatch(setUser({ ...currentUser, ...fresh.data }));
                        }
                    } catch {
                        /* non-fatal — Redux refreshes on next app load */
                    }
                }
                setLoading(false);
                const autopilotWillSchedule =
                    sharedListing && autopilotEnabled === true;
                toast.success(
                    <CustomToast
                        message={
                            autopilotWillSchedule
                                ? "Listing creation initiated. Auto Pilot will schedule posts for this listing automatically."
                                : "Listing creation initiated."
                        }
                        type="success"
                    />,
                    { autoClose: autopilotWillSchedule ? 6000 : 3000 }
                );
                // First-time Save & Auto-Sync always opens the setup wizard, even when the
                // default-type lookup found nothing: its first step asks for the property
                // types, which is how a commercial-only agent gets their listings at all.
                navigate(
                    "/listings",
                    saveMlsAgentDetails
                        ? { state: { openMlsReview: "setup" } }
                        : undefined
                );
            } else if (response.status === 410) {
                setLoading(false);
                toast.error(
                    <CustomToast
                        message="Your Listing Creation Limit has been reached. Please upgrade your plan to create more listings."
                        type="error"
                    />,
                    { autoClose: 5000 }
                );
            } else {
                throw new Error(
                    "Failed to initiate listing creation. Please try again."
                );
            }
        } catch (error) {
            const mlsDisplayBlocked = isMlsDisplayBlock(error);
            const feedOutage = getMlsFeedOutageMessage(error);
            if (feedOutage) {
                setLoading(false);
                toast.error(<CustomToast message={feedOutage} type="error" />, {
                    autoClose: 6000,
                });
            } else if ((error as any).response?.status === 410) {
                setLoading(false);
                setPlanLimitOpen(true);
            } else if (isAutopilotOwnListingBlock(error)) {
                setLoading(false);
                setAutopilotBlockOpen(true);
            } else if (isAutopilotTypeNotSyncedBlock(error)) {
                setLoading(false);
                setBlockedPropertyType(getBlockedPropertyTypeLabel(error));
                setTypeNotSyncedOpen(true);
            } else if (mlsDisplayBlocked) {
                // MB-1430: the MLS forbids internet display for this listing.
                // (A hidden address no longer refuses the import.)
                setLoading(false);
                toast.error(
                    <CustomToast
                        message="Your MLS has this listing marked as not displayable on the internet, so it can't be imported. Update the listing in the MLS to allow it."
                        type="error"
                    />,
                    { autoClose: 6000 }
                );
                setTimeout(() => navigate("/listings"), 1500);
            } else {
                console.error(error);
                setLoading(false);
                toast.error(
                    <CustomToast
                        message={getApiRefusalMessage(
                            error,
                            "An error occurred while creating the listing. Please try again."
                        )}
                        type="error"
                    />,
                    {
                        autoClose: 5000,
                    }
                );
            }
        }
    };

    if (!listing) {
        setTimeout(() => {
            window.location.href = "/listings";
        }, 3000);
        return <LoadingScreen />;
    }
    return (
        <main
            id="media-adder"
            className={`flex overflow-auto flex-col justify-center items-center min-h-full shadow-[0px_4px_4px_rgba(0,0,0,0.25)] ${
                isMobile ? "px-0 bg-white" : "bg-background"
            }`}
        >
            {loading && <LoadingScreen />}
            {/* Plan limit popup using PopoverContent */}
            {planLimitOpen && (
                <PlanLimitReached
                    open={planLimitOpen}
                    onClose={() => {
                        setPlanLimitOpen(false);
                        navigate("/listings");
                    }}
                    type="listing"
                />
            )}
            {/* MB-1424: own-listing hard block - Autopilot ingests these itself */}
            <AutopilotOwnListingBlocked
                open={autopilotBlockOpen}
                onClose={() => {
                    setAutopilotBlockOpen(false);
                    navigate("/listings");
                }}
            />
            {/* MB-1506: the type is switched off in MLS Agent Profile - the
                dialog walks the user to the setting that turns it on. */}
            <AutopilotTypeNotSyncedBlocked
                open={typeNotSyncedOpen}
                propertyTypeLabel={blockedPropertyType}
                onClose={() => {
                    setTypeNotSyncedOpen(false);
                    navigate("/listings");
                }}
                onGoToSettings={() => {
                    setTypeNotSyncedOpen(false);
                    navigate("/settings/mls-details");
                }}
            />
            <div
                className={`bg-white flex flex-col ${
                    isMobile ? "w-[90%]" : "w-[70%]"
                } p-10`}
            >
                <h1 className="mb-6 text-primary text-3xl font-bold leading-none text-center">
                    Add Additional Information
                </h1>
                <div className="space-y-4">
                    <InputField
                        label="Description"
                        name="legalDescription"
                        placeholder="Description"
                        value={listing?.legalDescription}
                        onChange={handleInputChange}
                        type="textarea"
                        rows={6}
                        className="w-full"
                        disabled={true}
                    />

                    <div className="bg-gray-100 p-3 rounded-md">
                        <p className="text-sm text-[#d50000]">
                            <strong className="text-primary">Warning:</strong>{" "}
                            The property description is less than 500
                            characters. For the best quality posts, consider
                            adding more details about key features such as
                            recent upgrades, unique architectural elements,
                            nearby attractions, or community amenities.
                        </p>
                    </div>

                    <InputField
                        label="Additional Content"
                        name="additionalContent"
                        placeholder="Additional Content"
                        value={listing?.additionalContent || ""}
                        onChange={handleInputChange}
                        type="textarea"
                        rows={6}
                        className="w-full"
                    />
                </div>
                <div className="mt-6 flex justify-between items-center">
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setCurrentStep(2);
                        }}
                    >
                        Back
                    </Button>
                    <Button onClick={handleSave}>Next</Button>
                </div>
            </div>
        </main>
    );
};

export default DescriptionAdder;
